/**
 * リソース使用率円グラフコンポーネント
 *
 * EChartsを使用してリソース使用率をドーナツチャートで表示
 */

import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import { Card, Typography } from 'antd';
import type { EChartsOption } from 'echarts';
import { THEME_COLORS, STATUS_COLORS } from '@/shared/constants/colors';
import { RESOURCE_THRESHOLDS, RESOURCE_LABELS, type ResourceType } from '../types/serverStatus.types';

const { Text } = Typography;

// ===== 定数 =====

const CHART_CONSTANTS = {
  /** チャートの高さ（モバイル） */
  CHART_HEIGHT_MOBILE: 180,
  /** チャートの高さ（デスクトップ） */
  CHART_HEIGHT_DESKTOP: 200,
  /** ドーナツの内側半径 */
  INNER_RADIUS: '55%',
  /** ドーナツの外側半径 */
  OUTER_RADIUS: '75%',
  /** パーセント表示のフォントサイズ（モバイル） */
  FONT_SIZE_MOBILE: 18,
  /** パーセント表示のフォントサイズ（デスクトップ） */
  FONT_SIZE_DESKTOP: 24,
  /** ラベルのフォントサイズ */
  LABEL_FONT_SIZE: 12,
} as const;

// ===== Props =====

interface ResourcePieChartProps {
  readonly type: ResourceType;
  readonly usedPercent: number;
  /** 絶対値の補足表示（例: "6.6 / 12.0 GB"）。指定時のみチャート下に表示 */
  readonly subLabel?: string;
  readonly isMobile: boolean;
}

// ===== コンポーネント =====

/**
 * 使用率に応じた色を取得
 */
const getStatusColor = (usedPercent: number): string => {
  if (usedPercent >= RESOURCE_THRESHOLDS.CRITICAL) {
    return STATUS_COLORS.ERROR;
  }
  if (usedPercent >= RESOURCE_THRESHOLDS.WARNING) {
    return STATUS_COLORS.WARNING;
  }
  return STATUS_COLORS.ACTIVE;
};

export const ResourcePieChart: React.FC<ResourcePieChartProps> = ({
  type,
  usedPercent,
  subLabel,
  isMobile,
}) => {
  const label = RESOURCE_LABELS[type];
  const statusColor = getStatusColor(usedPercent);
  const freePercent = Math.max(0, 100 - usedPercent);
  const chartHeight = isMobile
    ? CHART_CONSTANTS.CHART_HEIGHT_MOBILE
    : CHART_CONSTANTS.CHART_HEIGHT_DESKTOP;
  const fontSize = isMobile
    ? CHART_CONSTANTS.FONT_SIZE_MOBILE
    : CHART_CONSTANTS.FONT_SIZE_DESKTOP;

  const option: EChartsOption = useMemo(() => ({
    tooltip: {
      trigger: 'item',
      formatter: (params: unknown) => {
        const p = params as { name: string; value: number };
        return `${p.name}: ${p.value.toFixed(1)}%`;
      },
    },
    legend: {
      show: false,
    },
    series: [
      {
        name: label,
        type: 'pie',
        radius: [CHART_CONSTANTS.INNER_RADIUS, CHART_CONSTANTS.OUTER_RADIUS],
        center: ['50%', '50%'],
        avoidLabelOverlap: false,
        itemStyle: {
          borderRadius: 4,
          borderColor: '#fff',
          borderWidth: 2,
        },
        label: {
          show: true,
          position: 'center',
          formatter: () => `${usedPercent.toFixed(1)}%`,
          fontSize: fontSize,
          fontWeight: 'bold',
          color: statusColor,
        },
        emphasis: {
          label: {
            show: true,
            fontSize: fontSize,
            fontWeight: 'bold',
          },
        },
        labelLine: {
          show: false,
        },
        data: [
          {
            value: usedPercent,
            name: '使用中',
            itemStyle: { color: statusColor },
          },
          {
            value: freePercent,
            name: '空き',
            itemStyle: { color: THEME_COLORS.INFO + '30' },
          },
        ],
      },
    ],
  }), [label, usedPercent, freePercent, statusColor, fontSize]);

  return (
    <Card
      size="small"
      style={{
        textAlign: 'center',
        height: '100%',
      }}
      bodyStyle={{
        padding: isMobile ? 8 : 12,
      }}
    >
      <Text strong style={{ display: 'block', marginBottom: 4 }}>
        {label}
      </Text>
      <ReactECharts
        option={option}
        style={{ height: chartHeight, width: '100%' }}
        opts={{ renderer: 'svg' }}
        notMerge={true}
      />
      {subLabel !== undefined && (
        <Text type="secondary" style={{ display: 'block', marginTop: 2, fontSize: 12 }}>
          {subLabel}
        </Text>
      )}
    </Card>
  );
};
